import React, { useState } from 'react';
import { Users, UserCheck, Landmark, Settings, DollarSign, Search, Database, Clock, Shield, LogOut, Menu, X, Bell, ChevronDown } from 'lucide-react';
interface DashboardLayoutProps {
  children: React.ReactNode;
  activeModule: string;
  onModuleChange: (module: string) => void;
}
export function DashboardLayout({
  children,
  activeModule,
  onModuleChange
}: DashboardLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const modules = [{
    id: 'participants',
    label: 'Participant Management',
    icon: Users
  }, {
    id: 'aliases',
    label: 'Alias Management',
    icon: UserCheck
  }, {
    id: 'liquidity',
    label: 'Liquidity Vault',
    icon: Landmark
  }, {
    id: 'settlement',
    label: 'Settlement Config',
    icon: Settings
  }, {
    id: 'fees',
    label: 'Fee Management',
    icon: DollarSign
  }, {
    id: 'investigations',
    label: 'Investigation',
    icon: Search
  }, {
    id: 'repositories',
    label: 'Repositories',
    icon: Database
  }, {
    id: 'constraints',
    label: 'Constraints',
    icon: Clock
  }];
  return <div className="min-h-screen bg-slate-50 flex font-sans text-slate-900" data-id="element-10">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-60 bg-bank-navy text-white transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 flex flex-col`} data-id="element-11">
        {/* Logo Area */}
        <div className="h-20 flex flex-col items-start justify-center px-6 border-b border-white/10" data-id="element-12">
          <div className="flex items-center space-x-3" data-id="element-13">
            <Shield className="h-8 w-8 text-bank-gold fill-current" data-id="element-14" />
            <div className="flex flex-col" data-id="element-15">
              <span className="text-xs font-bold text-white tracking-wider uppercase" data-id="element-16">
                Admin Back Office
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-6 px-3" data-id="element-17">
          <div className="mb-2 px-3" data-id="element-18">
            <span className="text-xs font-semibold text-slate-400 tracking-wider uppercase" data-id="element-19">
              Core Modules
            </span>
          </div>
          <nav className="space-y-1" data-id="element-20">
            {modules.map(module => {
            const Icon = module.icon;
            const isActive = activeModule === module.id;
            return <button key={module.id} onClick={() => onModuleChange(module.id)} className={`w-full flex items-center text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-bank-gold text-bank-navy shadow-sm' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`} data-id="element-21">
                  <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${isActive ? 'text-bank-navy' : 'text-slate-400'}`} data-id="element-22" />
                  <span className="truncate" data-id="element-23">{module.label}</span>
                </button>;
          })}
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="p-4 border-t border-white/10 bg-bank-navy" data-id="element-24">
          <button className="flex items-center text-slate-300 hover:text-white transition-colors mb-4 px-2" data-id="element-25">
            <LogOut className="h-5 w-5 mr-3" data-id="element-26" />
            <span className="text-sm font-medium" data-id="element-27">Sign Out</span>
          </button>
          <div className="px-2" data-id="element-28">
            <p className="text-xs text-slate-500 font-mono" data-id="element-29">
              v2.4.0-stable | Secure Connection
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden" data-id="element-30">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-6 shadow-sm z-10" data-id="element-31">
          <div className="flex items-center" data-id="element-32">
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="lg:hidden text-slate-500 mr-4" data-id="element-33">
              <Menu className="h-6 w-6" data-id="element-34" />
            </button>

            {/* Search */}
            <div className="relative hidden md:block" data-id="element-35">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-36" />
              <input type="text" placeholder="Search transactions, institutions, or logs..." className="pl-10 pr-4 py-2 w-96 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bank-navy/20 focus:border-bank-navy" data-id="element-37" />
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-6" data-id="element-38">
            <button className="relative text-slate-400 hover:text-slate-600" data-id="element-39">
              <Bell className="h-6 w-6" data-id="element-40" />
              <span className="absolute top-0 right-0 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white" data-id="element-41"></span>
            </button>

            <div className="flex items-center pl-6 border-l border-slate-200" data-id="element-42">
              <div className="text-right mr-3 hidden sm:block" data-id="element-43">
                <p className="text-sm font-bold text-bank-navy leading-none" data-id="element-44">
                  Sarah Jenkins
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-45">Senior Supervisor</p>
              </div>
              <button className="flex items-center" data-id="element-46">
                <div className="h-9 w-9 rounded-full bg-bank-navy text-white flex items-center justify-center font-bold text-sm" data-id="element-47">
                  SJ
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400 ml-2" data-id="element-48" />
              </button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-8 bg-slate-50/50" data-id="element-49">
          <div className="max-w-7xl mx-auto space-y-8" data-id="element-50">{children}</div>
        </main>
      </div>
    </div>;
}